import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from 'common-ui/button';
import Input from 'common-ui/input';
import MobileList from 'common-ui/mobileList';
import ResetButton from 'common-ui/resetButton';
import { AsyncSelect } from 'common-ui/select';
import { Table } from 'common-ui/table';
import { getOffers } from 'features/sales/salesActions';
import { selectOffers, selectOffersLoading } from 'features/sales/salesSlice';
import useDebounce from 'hooks/useDebounce';
import Tag from 'pages/components/tag';
import { FilterBox, ResetBox } from 'pages/inventory/categorySinglePage/filter/Filter.styles';

import {
  BtnWrapper,
  EllipsisCell,
  FilterSelectBox,
  LeadFilterBox,
  LeadSourceFilter,
  ShiftControl,
} from '../Sales.styles';
import Create from './Create';
import useSearchData from './useSearchData';

const Offers = () => {
  const dispatch = useDispatch();
  const { searchData, setSearchData } = useSearchData();

  const [searchTerm, setSearchTerm] = useState(searchData.search || '');
  const debouncedName = useDebounce(searchTerm, 500);

  const userType = localStorage.getItem('userType');
  const access = userType === 'Super Admin' || userType === 'General Manager';

  const [editData, setEditData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const data = useSelector(selectOffers);
  const isLoading = useSelector(selectOffersLoading);

  useEffect(() => {
    dispatch(getOffers(searchData));
  }, [dispatch, searchData]);

  useEffect(() => {
    setSearchData({ search: debouncedName });
  }, [debouncedName]);

  const COLUMNS = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (val) => <EllipsisCell>{val}</EllipsisCell>,
    },
    {
      title: 'Status',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (val) => <Tag type="statuses" variant={val ? 'Enabled' : 'Disabled'} />,
    },
    ...(access
      ? [
          {
            title: 'Edit',
            key: 'edit',
            width: 100,
            render: (_, row) => (
              <ShiftControl
                onClick={() => {
                  setEditData({
                    ...row,
                    uuid: row.id,
                    enabled: row.isEnabled,
                  });
                  setShowModal(true);
                }}
              />
            ),
          },
        ]
      : []),
  ];

  const mobileColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (_, row) => <EllipsisCell>{row.name}</EllipsisCell>,
    },
    {
      title: 'Status',
      dataIndex: 'isEnabled',
      render: (_, row) => (
        <EllipsisCell>{row.isEnabled ? 'Enabled' : 'Disabled'}</EllipsisCell>
      ),
    },
  ];

  const handleChangeName = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSearchData({
      search: '',
      status: 'all',
      page: 1,
    });
  };

  const handleChangeStatus = (option) => {
    setSearchData({
      status: option?.value || 'all',
    });
  };

  const handlePageChange = (page) => {
    setSearchData({ page });
  };

  const handleCloseModal = () => {
    setEditData(null);
    setShowModal(false);
  };

  return (
    <>
      <LeadSourceFilter>
        <LeadFilterBox>
          <FilterBox>
            <Input placeholder="Search..." value={searchTerm} onChange={handleChangeName} />
          </FilterBox>

          <FilterSelectBox>
            <AsyncSelect
              value={searchData.status}
              options={[
                { label: 'All', value: 'all' },
                { label: 'Enabled', value: 'enabled' },
                { label: 'Disabled', value: 'disabled' },
              ]}
              onChange={handleChangeStatus}
            />
          </FilterSelectBox>

          <ResetBox>
            <ResetButton onClick={handleResetFilters} />
          </ResetBox>
        </LeadFilterBox>

        {access && (
          <BtnWrapper>
            <Button secondary onClick={() => setShowModal(true)}>
              + Add Offer
            </Button>
          </BtnWrapper>
        )}
      </LeadSourceFilter>

      <MobileList
        columns={mobileColumns}
        data={data?.data || []}
        currentPage={searchData.page}
        totalPages={data?.totalPages}
        onPaginationChange={handlePageChange}
      />

      <Table
        data={data?.data || []}
        columns={COLUMNS}
        currentPage={searchData.page}
        totalPages={data?.totalPages}
        onPaginationChange={handlePageChange}
        loading={isLoading}
        emptyText="No offers found"
      />

      {showModal && (
        <Create
          isEdit={!!editData}
          initialData={editData}
          activeTab="Offers"
          onCloseEdit={handleCloseModal}
          hideButton
        />
      )}
    </>
  );
};

export default Offers;
